<template>
  <v-card
    class="checklist-editor"
  >
    <v-banner
      class="checklist-banner"
      icon="mdi-format-list-checks"
      single-line
    >
      <h2>
        <span
          v-if="hasItems"
        >
          <v-text-field
            v-model="docketEntry.checklist.title"
            dense
            hide-details
            outlined
          />
        </span>

        <span
          v-else
          class="text--disabled"
        >
          No Checklist defined
        </span>
      </h2>

      <template #actions>
        <v-tooltip
          bottom
        >
          <template #activator="{on, attrs}">
            <v-btn
              icon
              outlined
              v-bind="attrs"
              v-on="on"
              @click="addItem"
            >
              <v-icon>mdi-playlist-plus</v-icon>
            </v-btn>
          </template>
          <span>Add Item</span>
        </v-tooltip>

        <v-menu
          v-if="hasItems"
          bottom
          left
        >
          <template #activator="{ on, attrs }">
            <v-btn
              icon
              v-bind="attrs"
              v-on="on"
            >
              <v-icon>mdi-dots-vertical</v-icon>
            </v-btn>
          </template>

          <v-list
            dense
          >
            <v-list-item
              v-if="!isNewChecklist"
              @click="resetChecklist"
            >
              <v-list-item-title>
                <v-icon
                  left
                >
                  mdi-restore
                </v-icon>
                Reset
              </v-list-item-title>
            </v-list-item>

            <v-list-item
              @click="deleteChecklist"
            >
              <v-list-item-title>
                <v-icon
                  left
                >
                  mdi-delete
                </v-icon>
                Delete
              </v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>

        <v-tooltip
          v-if="!hasItems"
          bottom
        >
          <template #activator="{on, attrs}">
            <v-btn
              icon
              outlined
              v-bind="attrs"
              v-on="on"
              @click="openImportChecklistDialog"
            >
              <v-icon>mdi-playlist-star</v-icon>
            </v-btn>
          </template>
          <span>Add Checklist from Library</span>
        </v-tooltip>
      </template>
    </v-banner>

    <v-list
      class="py-0"
      color="transparent"
      dense
    >
      <v-list-item
        v-if="!hasItems"
      >
        <v-list-item-content>
          <v-list-item-title
            class="font-weight-regular my-2 text--disabled text-body-2 text-center"
          >
            There are no entries on this Checklist.
          </v-list-item-title>
        </v-list-item-content>
      </v-list-item>

      <v-form
        v-else
        v-model="isFormValid"
      >
        <draggable
          v-model="docketEntry.checklist.items"
          v-bind="draggableOptions"
        >
          <div
            v-for="(item, index) of docketEntry.checklist.items"
            :key="index"
          >
            <v-divider v-if="index" />

            <v-list-item
              dense
            >
              <v-list-item-action>
                <div class="d-flex">
                  <v-icon
                    left
                    style="cursor: move"
                    @mouseover="enableDraggable"
                    @mouseout="disableDraggable"
                  >
                    mdi-reorder-horizontal
                  </v-icon>

                  <v-checkbox
                    v-model="item.done"
                  />
                </div>
              </v-list-item-action>

              <v-list-item-content
                class="py-0"
              >
                <v-text-field
                  :ref="`item-name-input--${index}`"
                  v-model="item.name"
                  class="cklst-text-input"
                  dense
                  hide-details
                  outlined
                  :rules="[rules.required]"
                  @keydown.enter.prevent="() => addItem(index + 1)"
                  @keyup.down="() => navigateToChecklistItem(index + 1)"
                  @keyup.up="() => navigateToChecklistItem(index - 1)"
                />
              </v-list-item-content>

              <v-list-item-action>
                <v-btn
                  :disabled="hasSingleItem"
                  icon
                  small
                  @click="() => removeItem(index)"
                >
                  <v-icon>
                    mdi-delete
                  </v-icon>
                </v-btn>
              </v-list-item-action>
            </v-list-item>
          </div>
        </draggable>
      </v-form>
    </v-list>

    <ImportChecklistDialog
      v-model="showImportChecklistDialog"
      @checklist-selected="onChecklistSelectedFromLibrary"
    />

    <ConfirmationDialog
      :visible="showDeleteConfirmDialog"
      cancel-button-label="NO"
      confirm-button-label="Yes"
      invert-action-buttons
      message="Delete the checklist?"
      title="Delete Checklist"
      @canceled="showDeleteConfirmDialog = false"
      @confirmed="_ => confirmDeleteChecklist()"
    />

    <ConfirmationDialog
      :visible="showResetConfirmDialog"
      cancel-button-label="NO"
      confirm-button-label="Yes"
      invert-action-buttons
      message="Reset the checklist?"
      title="Reset Checklist"
      @canceled="showResetConfirmDialog = false"
      @confirmed="_ => confirmResetChecklist()"
    />
  </v-card>
</template>

<script>
import { cloneDeep } from 'lodash'
import draggable from 'vuedraggable'
import { mapGetters } from 'vuex'

import { rules } from '@/utilities/validation-rules'

import { DOCKET_ENTRY_NAMESPACE } from '@/store/docket-entry'

import ConfirmationDialog from '@/components/Dialogs/Common/ConfirmationDialog.vue'
import ImportChecklistDialog from '@/components/Checklist/ChecklistImportDialog.vue'

export default {
  name: 'DocketEntryEditorChecklist',

  components: {
    ConfirmationDialog,
    ImportChecklistDialog,
    draggable,
  },

  data () {
    return {
      canDrag: false,
      rules,
      showDeleteConfirmDialog: false,
      showImportChecklistDialog: false,
      showResetConfirmDialog: false,
    }
  },

  computed: {
    docketEntry () {
      return this.editedDocketEntry
    },

    draggableOptions () {
      return { disabled: !this.canDrag }
    },

    hasItems () {
      return !!this.docketEntry.checklist?.items?.length
    },

    hasSingleItem () {
      return this.docketEntry.checklist?.items?.length === 1
    },

    isFormValid: {
      get () { return this.valid },
      set (val) { this.$emit('update:valid', val) },
    },

    isNewChecklist () {
      return !this.originalDocketEntry.checklist
    },

    ...mapGetters(DOCKET_ENTRY_NAMESPACE, [
      'editedDocketEntry',
      'originalDocketEntry',
    ]),
  },

  watch: {
    hasItems (val) {
      if (!val) this.$emit('update:valid', true)
    },
  },

  methods: {
    addItem (index) {
      if (!this.docketEntry.checklist) this.createEmptyChecklist()

      const item = { done: false, name: '' }

      if (isNaN(index) || index > this.docketEntry.checklist.items.length) index = this.docketEntry.checklist.items.length
      else if (index < 0) index = 0

      this.docketEntry.checklist.items.splice(index, 0, item)
      this.$nextTick().then(_ => { this.$refs[`item-name-input--${index}`][0].focus() })
    },

    confirmDeleteChecklist () {
      this.docketEntry.checklist = null
      this.showDeleteConfirmDialog = false
    },

    confirmResetChecklist () {
      const reset = cloneDeep(this.originalDocketEntry.checklist)
      if (reset?.items) reset.items.forEach(item => { item.done = false })
      this.docketEntry.checklist = reset
      this.showResetConfirmDialog = false
    },

    createEmptyChecklist () {
      this.docketEntry.checklist = {
        items: [],
        title: '<insert checklist title here>',
      }
    },

    deleteChecklist () {
      this.showDeleteConfirmDialog = true
    },

    disableDraggable () {
      this.canDrag = false
    },

    enableDraggable () {
      this.canDrag = true
    },

    navigateToChecklistItem (index) {
      if (index < 0 || index > this.docketEntry.checklist?.items?.length) return
      if (index === this.docketEntry.checklist?.items?.length) this.addItem(index)
      else this.$refs[`item-name-input--${index}`][0].focus()
    },

    onChecklistSelectedFromLibrary (checklist) {
      this.docketEntry.checklist = {
        items: checklist?.items?.map(item => {
          return {
            done: item.done,
            name: item.name,
          }
        }) || [],

        title: checklist?.title || '',
      }

      this.showImportChecklistDialog = false
    },

    openImportChecklistDialog () {
      this.showImportChecklistDialog = true
    },

    removeItem (index) {
      if (index < 0) return
      this.docketEntry.checklist.items.splice(index, 1)
    },

    resetChecklist () {
      this.showResetConfirmDialog = true
    },
  },
}
</script>

<style scoped>
.checklist-editor {
  .v-banner .v-banner__content,
  .v-banner .v-banner__text {
    overflow: visible;
  }
  .v-text-field--outlined fieldset {
    border-color: transparent;
  }
  .v-text-field--outlined .v-input__slot:hover fieldset,
  .v-text-field--outlined.v-input--is-focused fieldset {
    border-color: currentColor;
  }
  .v-text-field--outlined.error--text {
    background-color: rgba(255, 77, 77, .2);
  }
  .v-text-field--outlined.error--text fieldset {
    border-color: currentColor !important;
  }
}
</style>
